/**
 * EnTermsOfServicePage.jsx — Terms of Use (English)
 * Public route: /en/terms
 *
 * Required for Azure Marketplace Partner Center submission (Gate 2).
 * EN version of TermsOfServicePage.jsx — do NOT edit PT version here.
 *
 * Last updated: May 29, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';

const BRAND_GREEN = '#064e3b';

const Section = ({ title, children }) => (
  <section className="bg-white rounded-xl border-2 p-6" style={{ borderColor: '#e5e7eb' }}>
    <h2 className="text-2xl font-bold mb-4" style={{ color: BRAND_GREEN }}>{title}</h2>
    {children}
  </section>
);

export default function EnTermsOfServicePage() {
  return (
    <>
      <SEOHead
        title="Terms of Use"
        description="VUKZ AI Terms of Use — rights, obligations, acceptable use policy and governing law for the VUKZ Sports Intelligence platform."
        path="/en/terms"
        lang="en"
        alternatePath="/termos"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="text-sm text-gray-500 hover:underline">vukz.ai</Link>
            <span className="text-gray-300">/</span>
            <span className="text-sm text-gray-700 font-medium">Terms of Use</span>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ color: BRAND_GREEN }}>
            Terms of Use and Service
          </h1>
          <p className="text-sm text-gray-500">Last updated: May 29, 2026</p>
          <div className="mt-3 flex gap-3 text-xs">
            <Link to="/termos" className="text-gray-400 hover:underline">🇧🇷 Versão em Português</Link>
            <span className="text-gray-300">·</span>
            <Link to="/en/privacy" className="hover:underline" style={{ color: BRAND_GREEN }}>Privacy Policy</Link>
            <span className="text-gray-300">·</span>
            <Link to="/en/sla" className="hover:underline" style={{ color: BRAND_GREEN }}>SLA</Link>
          </div>
        </div>

        <div className="space-y-6 text-gray-700">

          <Section title="1. Acceptance of Terms">
            <p className="leading-relaxed">
              By accessing and using the VUKZ platform ("Platform", "Service", "we" or "our"),
              owned and operated by <strong>Arena Stats Ltda. (CNPJ 61.698.906/0001-16, Brazil)</strong>,
              trading as <strong>VUKZ AI</strong>, you ("User", "Customer", "you") agree to comply
              with and be bound by these Terms of Use. If you do not agree with any part of these
              terms, do not use our services.
            </p>
          </Section>

          <Section title="2. Description of Service">
            <p className="mb-3">
              VUKZ is a B2B SaaS platform providing a Sports Intelligence and Governance Operating System, including:
            </p>
            <ul className="space-y-2 text-sm">
              {[
                'Performance OS: training planning, periodization, load monitoring and athlete evolution',
                'SwimVision & TrackVision: computer vision for swimming and athletics performance analysis',
                'Rankings and competition data: structured results from official competitions',
                'Governance Intelligence OS: organizational governance, compliance, financial planning and data management',
                'AI Agents: automated analysis, reports and recommendations via embedded AI agents',
                'Social and collaboration features: feed, messaging, profiles and community',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="3. Account Registration and Access">
            <p className="mb-3 leading-relaxed">
              Access to the platform requires account registration. By registering, you represent that:
            </p>
            <ul className="space-y-2 text-sm mb-4">
              {[
                'You are at least 18 years old, or a legally authorized minor with parental consent',
                'The information you provide is accurate and up to date',
                'You are authorized to accept these terms on behalf of your organization (if applicable)',
                'You are responsible for maintaining the confidentiality of your credentials',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600">
              Multi-tenant accounts: each organization (institution/tenant) has its own isolated data
              environment. Tenant administrators are responsible for managing access and permissions
              within their organization.
            </p>
          </Section>

          <Section title="4. Subscription Plans and Payment">
            <p className="mb-3 leading-relaxed">
              VUKZ is offered under subscription plans (Sports Module, Sports Complete Suite, Sports
              Enterprise, Governance Starter, Governance Pro, Governance Enterprise). Plan details,
              pricing and included features are described at{' '}
              <a href="https://www.vukz.ai" className="underline" style={{ color: BRAND_GREEN }}>
                www.vukz.ai
              </a>{' '}
              and may be updated periodically.
            </p>
            <ul className="space-y-2 text-sm">
              {[
                'Azure Marketplace customers: billing and subscription management are handled by Microsoft Partner Center',
                'Direct customers: billing is processed by VUKZ AI; payment is due monthly or annually in advance',
                'Unused credits are not refunded except as SLA credits (see SLA document)',
                'VUKZ AI reserves the right to adjust pricing with 30 days\' prior notice',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="5. Acceptable Use Policy">
            <p className="mb-3 leading-relaxed font-medium text-gray-800">
              You agree NOT to use the platform to:
            </p>
            <ul className="space-y-2 text-sm mb-4">
              {[
                'Upload, transmit or share illegal, defamatory, fraudulent or abusive content',
                'Reverse engineer, decompile or attempt to extract the source code',
                'Resell, sublicense or make the platform available to unauthorized third parties',
                'Conduct penetration testing, vulnerability scanning or stress testing without prior written consent',
                'Interfere with other users\' accounts or circumvent security measures',
                'Use automated bots to extract data at scale (scraping) without written authorization',
                'Process data for purposes not covered by these Terms or your subscription plan',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0 bg-red-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600">
              Violation of this policy may result in immediate account suspension without refund.
            </p>
          </Section>

          <Section title="6. Intellectual Property">
            <p className="mb-3 leading-relaxed">
              All platform content, software, AI models, visual designs, trademarks (VUKZ, SwimVision,
              TrackVision, Performance OS, Governance OS) and documentation are the exclusive
              intellectual property of Arena Stats Ltda. or its licensors.
            </p>
            <p className="leading-relaxed text-sm text-gray-600">
              Your <strong>sports data and content</strong> remain your property. By uploading data
              to the platform, you grant VUKZ AI a limited, non-exclusive license to process and
              display that data solely for the purpose of providing the contracted services and,
              with your explicit consent, for improving AI models (anonymized).
            </p>
          </Section>

          <Section title="7. Data Protection and Privacy">
            <p className="leading-relaxed">
              The collection and use of personal data is governed by our{' '}
              <Link to="/en/privacy" className="underline" style={{ color: BRAND_GREEN }}>
                Privacy Policy
              </Link>
              , which is incorporated by reference into these Terms. By using the platform,
              you consent to the data processing practices described therein in accordance with
              Brazil's LGPD (Lei 13.709/2018).
            </p>
          </Section>

          <Section title="8. Availability and SLA">
            <p className="leading-relaxed">
              VUKZ commits to 99.5% monthly uptime. Detailed availability commitments, support
              tiers, incident response times and credit procedures are described in our{' '}
              <Link to="/en/sla" className="underline" style={{ color: BRAND_GREEN }}>
                Service Level Agreement
              </Link>.
            </p>
          </Section>

          <Section title="9. Limitation of Liability">
            <p className="mb-3 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, VUKZ AI SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="space-y-2 text-sm mb-4">
              {[
                'Indirect, incidental, special, consequential or punitive damages',
                'Loss of profits, revenue, data or business opportunities',
                'Damages arising from third-party infrastructure failures (Azure, DNS, CDN)',
                'Decisions made based on platform data or AI recommendations',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600">
              In any case, VUKZ AI's total aggregate liability shall not exceed the amount paid
              by the customer in the 3 months preceding the event giving rise to the claim.
            </p>
          </Section>

          <Section title="10. Termination">
            <p className="mb-3 leading-relaxed">
              Either party may terminate the subscription in accordance with the cancellation
              policy of the applicable plan:
            </p>
            <ul className="space-y-2 text-sm">
              {[
                'Monthly plans: cancellation effective at end of the current billing cycle',
                'Annual plans: cancellation effective at end of the annual period; no pro-rata refund unless SLA breach applies',
                'Azure Marketplace: cancellation processed via Microsoft Partner Center',
                'VUKZ AI may terminate or suspend the account immediately for material breach of these Terms',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="11. Modifications to Terms">
            <p className="leading-relaxed">
              VUKZ AI reserves the right to update these Terms of Use at any time. Material changes
              will be communicated via email and/or platform notice at least{' '}
              <strong>15 days before</strong> the changes take effect. Continued use of the platform
              after the effective date constitutes acceptance of the updated terms.
            </p>
          </Section>

          <Section title="12. Governing Law and Jurisdiction">
            <p className="leading-relaxed">
              These Terms are governed by the laws of the <strong>Federative Republic of Brazil</strong>.
              Any disputes shall be submitted to the courts of the judicial district of{' '}
              <strong>Belo Horizonte, State of Minas Gerais, Brazil</strong>, with the parties
              expressly waiving any other jurisdiction, however privileged it may be.
            </p>
            <p className="mt-3 text-sm text-gray-600">
              For international customers, disputes may also be subject to applicable consumer
              protection laws in the customer's country of residence.
            </p>
          </Section>

          <Section title="13. Contact">
            <p className="leading-relaxed">
              For questions regarding these Terms, contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-xl text-sm">
              <p className="font-bold text-gray-800">Arena Stats Ltda. (VUKZ AI)</p>
              <p className="text-gray-600">CNPJ 61.698.906/0001-16</p>
              <p className="text-gray-600">Av. Presidente Antônio Carlos, 681 — Lagoinha, Belo Horizonte / MG, Brazil</p>
              <a href="mailto:legal@vukz.ai" className="underline mt-2 block" style={{ color: BRAND_GREEN }}>
                legal@vukz.ai
              </a>
            </div>
          </Section>

          {/* Footer */}
          <div className="pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
            <p>VUKZ AI · Arena Stats Ltda. · CNPJ 61.698.906/0001-16 · Belo Horizonte, Brazil</p>
            <p className="mt-2">
              <span className="mx-2 font-semibold" style={{ color: BRAND_GREEN }}>Terms of Use (this document)</span>
              ·
              <Link to="/en/privacy" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>Privacy Policy</Link>
              ·
              <Link to="/en/sla" className="hover:underline mx-2" style={{ color: BRAND_GREEN }}>SLA</Link>
            </p>
            <p className="mt-2 text-xs text-gray-400">Version 1.0 — May 29, 2026</p>
          </div>
        </div>
      </div>
    </>
  );
}
